import React, { useMemo } from 'react';
import { Sun, Moon, Sunrise, Sunset } from 'lucide-react';

function fmtTime(unix) {
  if (!unix) return '—';
  try {
    return new Date(unix * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

export default function SunCard({ data, loading }) {
  const sunrise = data?.sunrise;
  const sunset = data?.sunset;
  const isDay = data?.symbol?.is_day !== false;

  // Compute current time position along the day arc (0..1) – must be called unconditionally
  const { pct, daylightHrs } = useMemo(() => {
    if (!sunrise || !sunset) return { pct: 0, daylightHrs: 0 };
    const nowS = Math.floor(Date.now() / 1000);
    const span = Math.max(60, sunset - sunrise);
    const p = Math.min(1, Math.max(0, (nowS - sunrise) / span));
    const hrs = span / 3600;
    return { pct: p, daylightHrs: hrs };
  }, [sunrise, sunset]);

  if (loading) {
    return (
      <div className="puvio-card p-5" data-testid="sun-card-skeleton">
        <div className="puvio-skeleton h-6 w-32 mb-3" />
        <div className="puvio-skeleton h-2 w-full mt-4" />
        <div className="puvio-skeleton h-4 w-40 mt-3" />
      </div>
    );
  }

  if (!sunrise || !sunset) {
    return (
      <div className="puvio-card p-5" data-testid="sun-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-[var(--puvio-accent-text)]">Daylight</p>
            <p className="text-sm text-[var(--puvio-text-2)] mt-0.5">Not available</p>
          </div>
          <div className="w-10 h-10 rounded-2xl bg-[var(--puvio-bg-soft)] flex items-center justify-center">
            <Sun className="w-5 h-5 text-[var(--puvio-accent-text)]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="puvio-card p-5" data-testid="sun-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-[var(--puvio-accent-text)]">Daylight</p>
          <p className="text-sm text-[var(--puvio-text-2)] mt-0.5">{daylightHrs.toFixed(1)} hours of light today</p>
        </div>
        <div className="w-10 h-10 rounded-2xl bg-[var(--puvio-bg-soft)] flex items-center justify-center">
          {isDay ? <Sun className="w-5 h-5 text-[#FFB020]" /> : <Moon className="w-5 h-5 text-[#7A8A80]" />}
        </div>
      </div>

      {/* Daylight bar with marker */}
      <div className="mt-4 relative h-3 rounded-full overflow-visible" data-testid="sun-daylight-bar">
        <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(90deg, #FFB020 0%, #FFE96A 25%, #FFD86A 50%, #FFB36A 80%, #6B5B95 100%)' }} />
        <div className="absolute -top-1 w-5 h-5 rounded-full bg-white border-2 border-[#0E1611] shadow-md transition-all" style={{ left: `calc(${pct * 100}% - 10px)` }} aria-hidden="true">
          <div className="absolute inset-1 rounded-full bg-[#FFB020]" />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-[var(--puvio-text-2)]">
          <span className="w-7 h-7 rounded-full bg-[#FFF3CC] flex items-center justify-center"><Sunrise className="w-3.5 h-3.5 text-[#E07A00]" /></span>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--puvio-text-3)]">Sunrise</p>
            <p className="text-sm font-semibold text-[#0F1A13]" data-testid="sun-sunrise">{fmtTime(sunrise)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[var(--puvio-text-2)]">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--puvio-text-3)]">Sunset</p>
            <p className="text-sm font-semibold text-[#0F1A13]" data-testid="sun-sunset">{fmtTime(sunset)}</p>
          </div>
          <span className="w-7 h-7 rounded-full bg-[#E8DEF0] flex items-center justify-center"><Sunset className="w-3.5 h-3.5 text-[#6B5B95]" /></span>
        </div>
      </div>
    </div>
  );
}
